import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Shield, Mail, Phone } from 'lucide-react';

const AnimatedSection = ({ children, className = '', delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
      transition={{ duration: 0.6, delay, ease: "easeOut" }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

const PrivacyPolicy = () => {
  const sections = [
    {
      title: "1. Scope of This Privacy Policy",
      content: `This Privacy Policy applies to:
      
• Visitors of the ProactAI websites (proactai.ai and proactai.in)
• Individuals submitting inquiries, demo requests, or forms
• Recipients of our email marketing campaigns
• Business clients using our retail vision intelligence solutions
• CCTV video data processed on behalf of clients

This policy does not apply to third-party websites linked from our site or to client-owned CCTV systems beyond the scope of contracted analytics services.`
    },
    {
      title: "2. Information We Collect",
      content: `2.1 Information You Provide

We may collect personal data such as your name, business email address, phone number, company name, job role, industry, number of stores, and any information you submit via contact forms, demo requests, or direct communication.

2.2 Information Automatically Collected

When you browse our website, we automatically collect limited technical and usage data such as IP address (anonymized where applicable), device type, browser, operating system, approximate location, pages visited, session duration, and interaction patterns using analytics tools.`
    },
    {
      title: "3. Purpose of Data Processing",
      content: `We process personal data strictly for legitimate and disclosed purposes, including:

• Responding to inquiries and demo requests
• Providing our retail intelligence services
• Running email campaigns and newsletters
• Improving website performance and user experience
• Analysing website traffic and user behaviour
• Ensuring security and preventing fraud
• Delivering AI-powered retail analytics solutions
• Meeting legal and regulatory obligations`
    },
    {
      title: "4. Legal Basis for Processing",
      content: `We process personal data based on:

• Consent: For forms, cookies, and marketing emails
• Legitimate Interest: For analytics, service improvement, and security
• Contractual Necessity: For client services and analytics delivery
• Legal Obligation: For regulatory compliance`
    },
    {
      title: "5. DPDP Act (India) Compliance",
      content: `In accordance with the Digital Personal Data Protection (DPDP) Act, 2023, ProactAI:

• Collects only necessary data for stated purposes
• Processes data only for disclosed purposes
• Obtains consent where required by law
• Allows withdrawal of consent at any time
• Protects data using reasonable security safeguards
• Provides mechanisms for data access and correction

Requests can be sent to privacy@proactai.in.`
    },
    {
      title: "6. Data Retention",
      content: `We retain personal data only as long as necessary:

• Website inquiries & marketing data: Up to 24 months from last interaction
• Client communication data: Contract duration + 12 months
• CCTV video footage (analytics): Typically 7–90 days, unless extended for legal reasons
• Analytics & cookie data: Retained as per respective platform policies

After the retention period expires, data is securely deleted or anonymised.`
    },
    {
      title: "7. Cookies & Consent Management",
      content: `Our website uses various types of cookies:

• Essential Cookies: Required for basic website functionality
• Analytics Cookies: To understand how visitors interact with our website
• Functional Cookies: To enable certain features and personalisation
• Marketing Cookies: For targeted advertising and remarketing

Users can accept, reject, or manage cookies via our consent mechanism or browser settings.`
    },
    {
      title: "8. Use of Analytics & Tracking Tools",
      content: `We use the following analytics and tracking tools:

• Google Analytics: For website performance and user behaviour analysis
• Meta Pixel: For conversion tracking and ad optimisation
• Other Analytics Tools: For heatmaps, session recordings, and user experience improvement

All tools are configured to respect user privacy preferences and applicable regulations.`
    },
    {
      title: "9. Email Marketing & Communication",
      content: `We may send newsletters, service updates, product announcements, or promotional campaigns to users who have opted in.

• All marketing emails include an unsubscribe option
• Consent may be withdrawn at any time
• We respect Do Not Disturb (DND) registries
• Marketing frequency is kept reasonable and relevant`
    },
    {
      title: "10. Use of CCTV Video Footage for Analytics",
      content: `ProactAI processes CCTV footage solely on behalf of clients for:

• Footfall counting and traffic analysis
• Queue detection and wait time measurement
• Dwell time and engagement analytics
• Heatmaps and zone-based analytics
• Staff activity monitoring
• Operational efficiency insights
• Compliance and audit purposes

Facial Recognition & Automated Decision-Making: ProactAI does not perform facial recognition or biometric identification by default. Our analytics are designed to be non-identifying and do not produce legally binding automated decisions unless explicitly agreed upon and permitted by law.`
    },
    {
      title: "11. Data Sharing & Processors",
      content: `We may share data with trusted service providers including:

• Cloud hosting providers
• Analytics platforms
• Email service providers
• Customer relationship management systems
• Security and fraud prevention services

We do not sell personal data to third parties. All service providers are bound by data protection agreements.`
    },
    {
      title: "12. Server & Data Storage Location",
      content: `Data may be stored on secure servers in India or other jurisdictions where our service providers operate. We ensure appropriate safeguards are in place for any cross-border data transfers, including:

• Standard contractual clauses
• Adequacy decisions where applicable
• Binding corporate rules
• Consent where required`
    },
    {
      title: "13. Data Security Measures",
      content: `We implement robust security measures to protect personal data:

• Encryption in transit and at rest
• Access controls and authentication
• Regular security audits and assessments
• Employee training on data protection
• Incident response procedures
• Secure development practices`
    },
    {
      title: "14. Data Breach Response",
      content: `In case of a data breach, ProactAI will:

• Immediately assess the scope and impact
• Contain the breach and prevent further damage
• Notify affected parties within statutory timelines
• Report to relevant authorities as required by law
• Document the incident and remediation steps
• Implement measures to prevent recurrence`
    },
    {
      title: "15. Your Rights",
      content: `As a data subject, you have the following rights:

• Right to Access: Request a copy of your personal data
• Right to Correction: Request correction of inaccurate data
• Right to Deletion: Request deletion of your personal data
• Right to Withdraw Consent: Withdraw consent at any time
• Right to Data Portability: Receive your data in a portable format
• Right to Object: Object to certain types of processing

To exercise these rights, please contact us at privacy@proactai.in.`
    },
    {
      title: "16. Children's Privacy",
      content: `ProactAI does not knowingly collect personal data from children under 13 years of age. Our services are designed for business use and are not intended for children.

If we become aware that we have collected personal data from a child under 13, we will take steps to promptly delete such information.`
    },
    {
      title: "17. Policy Updates",
      content: `This Privacy Policy may be updated periodically to reflect changes in our practices, technologies, legal requirements, or other factors.

• The latest version will always be available on this page
• Material changes will be communicated via email or website notice
• Continued use of our services after updates constitutes acceptance`
    }
  ];

  return (
    <div data-testid="privacy-policy-page" className="bg-white">
      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-b from-secondary/50 to-white">
        <div className="container-custom text-center">
          <AnimatedSection>
            <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Shield className="w-8 h-8 text-primary" />
            </div>
            <h1 className="text-4xl lg:text-5xl font-extrabold font-heading text-foreground mb-6">
              Privacy Policy
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              ProactAI values the privacy of individuals and organizations interacting with our website and services. 
              This Privacy Policy explains how we collect, use, process, store, and protect your personal data.
            </p>
            <p className="text-sm text-muted-foreground mt-4">
              Last Updated: April 2026
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-12 border-b border-border">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection>
            <div className="prose prose-lg max-w-none">
              <p className="text-muted-foreground leading-relaxed">
                ProactAI ("ProactAI", "we", "our", or "us") is committed to protecting your privacy and ensuring 
                the security of your personal information. This Privacy Policy describes our practices regarding the 
                collection, use, and disclosure of information when you visit{' '}
                <a href="https://proactai.ai" className="text-primary hover:underline">https://proactai.ai</a> or{' '}
                <a href="https://proactai.in" className="text-primary hover:underline">https://proactai.in</a>, 
                engage with our communications, or use our AI-powered retail vision intelligence solutions.
              </p>
              <p className="text-muted-foreground leading-relaxed mt-4">
                This policy is governed by the Digital Personal Data Protection (DPDP) Act, 2023 (India) and, 
                where applicable, aligns with the GDPR, CCPA, and other international privacy regulations.
              </p>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Policy Sections */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-12">
            {sections.map((section, index) => (
              <AnimatedSection key={index} delay={index * 0.05}>
                <div className="border-b border-border pb-8 last:border-b-0">
                  <h2 className="text-xl font-bold font-heading text-foreground mb-4">
                    {section.title}
                  </h2>
                  <div className="text-muted-foreground leading-relaxed whitespace-pre-line">
                    {section.content}
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 bg-secondary/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection>
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold font-heading text-foreground mb-4">
                18. Contact & Grievance Redressal
              </h2>
              <p className="text-muted-foreground">
                For privacy questions, data requests, or grievances, please contact us:
              </p>
            </div>
            
            <div className="bg-white rounded-2xl p-8 shadow-sm border border-border">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">Email</h3>
                    <a href="mailto:privacy@proactai.in" className="text-primary hover:underline">
                      privacy@proactai.in
                    </a>
                    <p className="text-sm text-muted-foreground mt-1">
                      For privacy-related inquiries
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">Phone</h3>
                    <a href="tel:+919871621133" className="text-primary hover:underline">
                      +91 9871621133
                    </a>
                    <p className="text-sm text-muted-foreground mt-1">
                      Monday - Friday, 9 AM - 6 PM IST
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="mt-8 pt-6 border-t border-border">
                <h3 className="font-semibold text-foreground mb-2">Grievance Officer</h3>
                <p className="text-muted-foreground text-sm">
                  In accordance with the DPDP Act, 2023, if you have any grievances regarding 
                  the processing of your personal data, you may contact our Grievance Officer 
                  at <a href="mailto:grievance@proactai.in" className="text-primary hover:underline">grievance@proactai.in</a>. 
                  We will respond to your grievance within 30 days.
                </p>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicy;
